import { booleanPointInPolygon, distance, explode, nearestPoint, nearestPointOnLine } from '@turf/turf'
import type { AssetLocation } from '../../../data/locations.tsx'
import { type StrictPoint, strictPoint } from '../../../strict-point.ts'
import type { BaseReportForm } from './schema.ts'

export const snapPointInsideGeometry = (
	map: mapboxgl.Map,
	geometry: AssetLocation['geometry'],
	inputPoint: StrictPoint,
	options: { maxSnapDistance: { unit: 'pixels' | 'kilometers'; distance: number } },
): NonNullable<BaseReportForm['customPoint']> => {
	const point = strictPoint([
		Math.max(-180, Math.min(180, inputPoint.coordinates[0])),
		Math.max(-90, Math.min(90, inputPoint.coordinates[1])),
	])
	const closestPointInsideGeometry = closestPointInGeometry(geometry, point)
	if (!closestPointInsideGeometry) {
		return { ...point, isOutsideAssetGeometry: true }
	}

	if (options.maxSnapDistance.unit === 'kilometers') {
		const distanceInKm = distance(closestPointInsideGeometry, point, {
			units: 'kilometers',
		})
		const canBeSnapped = distanceInKm <= options.maxSnapDistance.distance
		return canBeSnapped ? { ...closestPointInsideGeometry, isOutsideAssetGeometry: false } : { ...point, isOutsideAssetGeometry: true }
	} else {
		const inputPointInScreenSpace = map.project(point.coordinates)
		const closestPointInScreenSpace = map.project(closestPointInsideGeometry.coordinates)

		const distanceInPixels = Math.sqrt(
			(inputPointInScreenSpace.x - closestPointInScreenSpace.x) ** 2 + (inputPointInScreenSpace.y - closestPointInScreenSpace.y) ** 2,
		)

		return distanceInPixels < options.maxSnapDistance.distance
			? { ...closestPointInsideGeometry, isOutsideAssetGeometry: false }
			: { ...point, isOutsideAssetGeometry: true }
	}
}

const closestPointInGeometry = (geometry: AssetLocation['geometry'], referencePoint: StrictPoint): StrictPoint | undefined => {
	switch (geometry.type) {
		case 'Point':
		case 'MultiPoint': {
			const points = explode(geometry)
			const closestPoint = nearestPoint(referencePoint, points, {
				units: 'kilometers',
			})

			return strictPoint(closestPoint.geometry)
		}
		case 'LineString':
		case 'MultiLineString': {
			// Note: this does not seem to be 100% precise. In high zoom levels,
			// there seems to be an unexpected discrepancy between input point and what this returns.
			const closestPoint = nearestPointOnLine(geometry, referencePoint)
			return strictPoint(closestPoint.geometry)
		}
		case 'Polygon':
		case 'MultiPolygon':
			// Note: Turf does not have function to determine "nearest point inside polygon to a reference point",
			// thus this only returns a value if the reference point is inside polygon. This should be fine for our purposes
			return booleanPointInPolygon(referencePoint, geometry) ? referencePoint : undefined
		default:
			geometry satisfies never
	}
}
